import React, {useEffect, useRef, useState} from 'react';
import {MapContainer, Marker, Popup, TileLayer, useMap} from 'react-leaflet';
import L from 'leaflet';
import {collection, doc, getDoc, getDocs} from "firebase/firestore";
import {analytics, db} from "../../firebase"; // Import Firebase config
import './Map.css';
import MarkerClusterGroup from 'react-leaflet-cluster';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {logEvent} from "firebase/analytics";
import {getAuth} from "firebase/auth";
import {useNavigate} from "react-router-dom";

// Correct the default marker icon path issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Define custom icons for AboutMap of excellence and patient organizations
const centerIcon = new L.Icon({
    iconUrl: 'hospital.svg', // Path to your center icon image
    iconSize: [41, 41], // Size of the icon
    iconAnchor: [12, 41], // Anchor point of the icon
    popupAnchor: [1, -34], // Point from which the popup should open relative to the iconAnchor
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'), // Use default shadow if desired
    shadowSize: [41, 41], // Size of the shadow
});

const otherCenterIcon = new L.Icon({
    iconUrl: 'oc.svg', // Path to your center icon image
    iconSize: [41, 41], // Size of the icon
    iconAnchor: [12, 41], // Anchor point of the icon
    popupAnchor: [1, -34], // Point from which the popup should open relative to the iconAnchor
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'), // Use default shadow if desired
    shadowSize: [41, 41], // Size of the shadow
});

const verifiedPatientOrganizationIcon = new L.Icon({
    iconUrl: 'vpo.svg', // Path to your organization icon image
    iconSize: [41, 41], // Size of the icon
    iconAnchor: [12, 41], // Anchor point of the icon
    popupAnchor: [1, -34], // Point from which the popup should open relative to the iconAnchor
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'), // Use default shadow if desired
    shadowSize: [41, 41], // Size of the shadow
});

const nonVerifiedPatientOrganizationIcon = new L.Icon({
    iconUrl: 'nvpo.svg', // Path to your organization icon image
    iconSize: [41, 41], // Size of the icon
    iconAnchor: [12, 41], // Anchor point of the icon
    popupAnchor: [1, -34], // Point from which the popup should open relative to the iconAnchor
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'), // Use default shadow if desired
    shadowSize: [41, 41], // Size of the shadow
});

// const MapLegend = () => {
//     return (<div className="map-legend">
//         {/*<h4>Υπόμνημα</h4>*/}
//         <div style={{display: 'flex', alignItems: 'center', marginBottom: '5px'}}>
//             <img
//                 src="hospital.svg"
//                 alt="Center of Excellence Icon"
//                 style={{width: '20px', height: '20px', marginRight: '5px'}}
//             />
//             Κέντρα Εμπειρογνωμοσύνης
//         </div>
//         <div style={{display: 'flex', alignItems: 'center', marginBottom: '5px'}}>
//             <img
//                 src="oc.svg"
//                 alt="Other Center Icon"
//                 style={{width: '20px', height: '20px', marginRight: '5px'}}
//             />
//             Ειδικές Μονάδες / Ιατρεία
//         </div>
//         <div style={{display: 'flex', alignItems: 'center'}}>
//             <img
//                 src="vpo.svg"
//                 alt="Patient Organization Icon"
//                 style={{width: '20px', height: '20px', marginRight: '5px'}}
//             />
//             Οργανώσεις Ασθενών μέλη Ε.Σ.Α.Ε
//         </div>
//         <div style={{display: 'flex', alignItems: 'center'}}>
//             <img
//                 src="nvpo.svg"
//                 alt="Patient Organization Icon"
//                 style={{width: '20px', height: '20px', marginRight: '5px'}}
//             />
//             Οργανώσεις Ασθενών μη μέλη Ε.Σ.Α.Ε
//         </div>
//     </div>);
// };

// Collapsible component for each category
const CollapsibleSection = ({title, children, isOpen, onToggle, itemCount, legendIcons}) => (
    <div className="collapsible-section">
        <div className="collapsible-header" onClick={onToggle} style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
            backgroundColor: '#f9f9f9',
            borderRadius: '5px',
            border: '1px solid #ccc'
        }}>
            <div style={{display: 'flex', alignItems: 'center'}}>
                <h4 style={{margin: 0, fontWeight: 'bold', fontSize: '1.1em', paddingRight: '10px'}}>{title}</h4>
                <span style={{fontSize: '0.9em', color: '#666'}}>({itemCount})</span>
                {legendIcons && legendIcons.map((icon, index) => (<img
                    key={index}
                    src={icon}
                    alt={`${title} icon ${index}`}
                    style={{width: '20px', height: '20px', marginLeft: '5px'}}
                />))}
            </div>
            <span className={`fas ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></span>
        </div>
        {isOpen && <div className="collapsible-content" style={{
            padding: '10px', borderTop: '1px solid #ccc', backgroundColor: '#fff'
        }}>{children}</div>}
    </div>);

// Component to automatically fit bounds to markers
const FitBoundsOnLoad = ({bounds, dataLoaded}) => {
    const map = useMap();
    useEffect(() => {
        if (dataLoaded && bounds.length > 0) {
            map.fitBounds(bounds);
        }
    }, [map, bounds, dataLoaded]);
    return null;
};

const MapComponent = () => {
    const auth = getAuth();
    const user = auth.currentUser; // Or use a hook/context to track auth state
    const navigate = useNavigate();

    const [centerLocations, setCenterLocations] = useState([]);
    const [otherCenterLocations, setOtherCenterLocations] = useState([]);
    const [organizationLocations, setOrganizationLocations] = useState([]);
    const [diseasesData, setDiseasesData] = useState([]); // Store diseases data
    const [dataLoaded, setDataLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState(''); // State to store search input
    const [searchResults, setSearchResults] = useState({
        diseases: [], centers: [], otherCenters: [], organizations: []
    });
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [isAffiliatedCentersOpen, setIsAffiliatedCentersOpen] = useState(false);
    const [isAffiliatedOtherCentersOpen, setIsAffiliatedOtherCentersOpen] = useState(false);
    const [isAffiliatedPatientOrganizationsOpen, setIsAffiliatedPatientOrganizationsOpen] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Ref to store references to markers
    const markersRef = useRef({});
    const markerClusterGroupRef = useRef(null);

    // State to track collapsible sections
    const [isDiseasesOpen, setIsDiseasesOpen] = useState(false);
    const [isCentersOpen, setIsCentersOpen] = useState(false);
    const [isOtherCentersOpen, setIsOtherCentersOpen] = useState(false);
    const [isOrganizationsOpen, setIsOrganizationsOpen] = useState(false);

    // Disease Monitoring
    const [selectedDisease, setSelectedDisease] = useState(null);

    useEffect(() => {
        const fetchMetadata = async () => {
            const metadataRef = doc(db, "metadata", "metadata"); // Collection: "metadata", Document ID: "metadata"
            const metadataDoc = await getDoc(metadataRef);
            if (metadataDoc.exists()) {
                const data = metadataDoc.data();
                setLastUpdated(data.last_updated?.toDate() || null); // Convert Firestore Timestamp to JavaScript Date
            }
        };

        fetchMetadata();
    }, []);

    useEffect(() => {
        // Fetch all data on component mount
        const fetchAllData = async () => {
            const centersSnapshot = await getDocs(collection(db, "centers_of_excellence"));
            const otherCentersSnapshot = await getDocs(collection(db, "other_centers"));
            const organizationsSnapshot = await getDocs(collection(db, "patient_organizations"));
            const diseasesSnapshot = await getDocs(collection(db, "disease"));

            // Process Centers of Excellence
            const centerLocations = centersSnapshot.docs
                .map(doc => ({...doc.data()}))
                .filter(center => center.Location !== null)
                .sort((a, b) => a.Name.localeCompare(b.Name, 'el'));

            // Process Centers of Excellence
            const otherCenterLocations = otherCentersSnapshot.docs
                .map(doc => ({...doc.data()}))
                .filter(center => center.Location !== null)
                .sort((a, b) => a.ClinicName.localeCompare(b.ClinicName, 'el'));

            // Process Patient Organizations
            const organizationLocations = organizationsSnapshot.docs
                .map(doc => ({...doc.data()}))
                .filter(org => org.Location !== null)
                .sort((a, b) => a.GreekName.localeCompare(b.GreekName, 'el'));

            // Process Diseases
            const diseasesData = diseasesSnapshot.docs
                .map(doc => ({...doc.data()}))
                .sort((a, b) => {
                    const aName = (a.GreekNames && a.GreekNames[0]) || (a.EnglishNames && a.EnglishNames[0]) || '';
                    const bName = (b.GreekNames && b.GreekNames[0]) || (b.EnglishNames && b.EnglishNames[0]) || '';
                    return aName.localeCompare(bName, 'el');
                });

            // Set all fetched data into state
            setCenterLocations(centerLocations.filter(center => center.Location !== null));
            setOtherCenterLocations(otherCenterLocations.filter(center => center.Location !== null));
            setOrganizationLocations(organizationLocations.filter(org => org.Location !== null));
            setDiseasesData(diseasesData);
            setDataLoaded(true);
        };

        fetchAllData();
    }, [db]);

    const handleMarkerClick = (entity) => {
        const marker = markersRef.current[entity.ID];
        if (marker) {
            // Open the cluster if necessary and then the popup
            if (markerClusterGroupRef.current) {
                markerClusterGroupRef.current.zoomToShowLayer(marker, () => {
                    marker.openPopup();
                });
            } else {
                // Open the popup if it's not in a cluster
                setTimeout(() => {
                    marker.openPopup();
                }, 1000);
            }
        }

    };

    const handleDiseaseSelect = (disease) => {
        setSelectedDisease(disease);
        setSelectedEntity(disease);
    };

    const renderRightPanelContent = () => {

        if (!selectedEntity) {
            return <p>Δεν έχει επιλεγεί οντότητα</p>;
        }

        if (selectedEntity.Type === "Disease") {
            return <div>
                <div className="selected-header">
                    <h4 className="info-title">Πληροφορίες για τη νόσο</h4>
                </div>
                {selectedEntity ? (<div className="selected-info">
                    {selectedEntity.GreekNames && selectedEntity.GreekNames.length > 0 && (
                        <p><strong>Ελληνικό Όνομα & Συνώνυμα:</strong> {selectedEntity.GreekNames.join(', ')}</p>)}
                    {selectedEntity.EnglishNames && selectedEntity.EnglishNames.length > 0 && (
                        <p><strong>Αγγλική Ονομασία & Συνώνυμα:</strong> {selectedEntity.EnglishNames.join(', ')}</p>)}
                    <p><strong>ORPHAcode:</strong> {selectedEntity.Orphacode}</p>
                    {selectedEntity.ICD10 && selectedEntity.ICD10.length > 0 && (
                        <p><strong>ICD-10: </strong> {selectedEntity.ICD10.join(' ')}</p>)}
                    {selectedEntity.ICD11 && selectedEntity.ICD11.length > 0 && (
                        <p><strong>ICD-11:</strong> {selectedEntity.ICD11.map((item, index) => (<a
                            key={index}
                            href={item.URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{marginRight: '10px'}}
                        >
                            {item.Name}
                        </a>))}</p>)}
                    {selectedEntity.ERN && selectedEntity.ERN.length > 0 && (
                        <p><strong>ERN:</strong> {selectedEntity.ERN.map((item, index) => (
                            <a key={index} href={item.URL} target="_blank" rel="noopener noreferrer"
                               style={{marginRight: '10px'}}>
                                {item.Name}
                            </a>))}</p>)}
                    {selectedEntity.GRDefinition &&
                        <p><strong>Ελληνικός ορισμός:</strong> {selectedEntity.GRDefinition}</p>}
                    {selectedEntity.ENDefinition &&
                        <p><strong>Αγγλικός ορισμός:</strong> {selectedEntity.ENDefinition}</p>}
                    <a href={`${selectedEntity.Orphanet}`}
                       target="_blank"
                       rel="noopener noreferrer" className="orphanet-link">
                        Orphanet
                    </a>
                    <a href={`https://pubmed.ncbi.nlm.nih.gov/?term=${selectedEntity.EnglishNames[0] || selectedEntity.GreekNames[0]}`}
                       target="_blank"
                       rel="noopener noreferrer" className="orphanet-link">
                        Άρθρα PubMed
                    </a>
                    <a href={`https://clinicaltrials.gov/search?cond=${selectedEntity.EnglishNames[0] || selectedEntity.GreekNames[0]}`}
                       target="_blank"
                       rel="noopener noreferrer" className="orphanet-link">
                        Κλινικές Δοκιμές (ClinicalTrials.gov)
                    </a>
                    <a href={`https://www.clinicaltrialsregister.eu/ctr-search/search?query=${selectedEntity.EnglishNames[0] || selectedEntity.GreekNames[0]}`}
                       target="_blank"
                       rel="noopener noreferrer" className="orphanet-link">
                        Κλινικές Δοκιμές (Clinical Trials Information System)
                    </a>
                    {affiliatedCenters && affiliatedCenters.length > 0 && (<CollapsibleSection
                        title="Ειδικά & Σχετικά Κέντρα Εμπειρογνωμοσύνης"
                        itemCount={affiliatedCenters.length}
                        isOpen={isAffiliatedCentersOpen}
                        onToggle={() => setIsAffiliatedCentersOpen(!isAffiliatedCentersOpen)}
                    >
                        <ul className="collapsible-list">
                            {affiliatedCenters.map((center, index) => (<li key={index} className="collapsible-list-item"
                                                                           onClick={() => handleMarkerClick(center)}
                                                                           style={{cursor: 'pointer'}}>
                                <div className="item-content">
                                    <span className="item-title">{center.Name}</span>
                                </div>
                            </li>))}
                        </ul>
                    </CollapsibleSection>)}
                    {affiliatedOtherCenters && affiliatedOtherCenters.length > 0 && (<CollapsibleSection
                        title="Ειδικές Μονάδες / Ιατρεία"
                        itemCount={affiliatedOtherCenters.length}
                        isOpen={isAffiliatedOtherCentersOpen}
                        onToggle={() => setIsAffiliatedOtherCentersOpen(!isAffiliatedOtherCentersOpen)}
                    >
                        <ul className="collapsible-list">
                            {affiliatedOtherCenters.map((center, index) => (
                                <li key={index} className="collapsible-list-item"
                                    onClick={() => handleMarkerClick(center)}
                                    style={{cursor: 'pointer'}}>
                                    <div className="item-content">
                                        <span className="item-title">{center.ClinicName}</span>
                                    </div>
                                </li>))}
                        </ul>
                    </CollapsibleSection>)}
                    {affiliatedPatientOrganizations && affiliatedPatientOrganizations.length > 0 && (<CollapsibleSection
                        title="Οργανώσεις Ασθενών"
                        itemCount={affiliatedPatientOrganizations.length}
                        isOpen={isAffiliatedPatientOrganizationsOpen}
                        onToggle={() => setIsAffiliatedPatientOrganizationsOpen(!isAffiliatedPatientOrganizationsOpen)}
                    >
                        <ul className="collapsible-list">
                            {affiliatedPatientOrganizations.map((center, index) => (
                                <li key={index} className="collapsible-list-item"
                                    onClick={() => handleMarkerClick(center)}
                                    style={{cursor: 'pointer'}}>
                                    <div className="item-content">
                                        <span className="item-title">{center.GreekName}</span>
                                    </div>
                                </li>))}
                        </ul>
                    </CollapsibleSection>)}
                </div>) : (<p>No disease selected</p>)}
            </div>
        }
    }

    // Calculate bounds from all markers
    const allLocations = [...centerLocations, ...organizationLocations];
    const bounds = allLocations.map(loc => [loc.Location.latitude, loc.Location.longitude]);

    const affiliatedCenters = selectedEntity?.Type === "Disease" && selectedEntity?.CentersOfExcellence?.map((centerRef) => {
        const centerId = typeof centerRef === 'object' && centerRef.id ? centerRef.id : centerRef;
        return centerLocations.find(center => center.ID === centerId);
    }).filter(center => center);

    const affiliatedOtherCenters = selectedEntity?.Type === "Disease" && selectedEntity?.OtherCenters?.map((centerRef) => {
        const centerId = typeof centerRef === 'object' && centerRef.id ? centerRef.id : centerRef;
        return otherCenterLocations.find(center => center.ID === centerId);
    }).filter(center => center);

    const affiliatedPatientOrganizations = selectedEntity?.Type === "Disease" && selectedEntity?.PatientOrganizations?.map((centerRef) => {
        const centerId = typeof centerRef === 'object' && centerRef.id ? centerRef.id : centerRef;
        return organizationLocations.find(center => center.ID === centerId);
    }).filter(center => center);

    const handleSearchChange = (event) => {
        const query = event.target.value;
        setSearchQuery(query);

        // Log search event to Firebase Analytics
        if (query) {
            logEvent(analytics, 'search', {
                search_term: query
            });
        }
    };

    // Perform search on local data when query changes
    useEffect(() => {
        const lowerCaseQuery = searchQuery.toLowerCase().trim();

        // Filter diseases based on name, Orphacode, ICD-10, and ICD-11
        const filteredDiseases = diseasesData.filter(disease => {
            const englishNameMatch = disease.EnglishNames?.some(name => name.toLowerCase().includes(lowerCaseQuery)) || false;
            const greekNameMatch = disease.GreekNames?.some(name => name.toLowerCase().includes(lowerCaseQuery)) || false;
            const orphacodeMatch = disease.Orphacode && disease.Orphacode.includes(lowerCaseQuery);
            const icd10Match = disease.ICD10 && disease.ICD10.some(code => code.toLowerCase().includes(lowerCaseQuery));

            return englishNameMatch || greekNameMatch || orphacodeMatch || icd10Match;
        });

        let filteredCenters = centerLocations;
        let filteredOtherCenters = otherCenterLocations;
        let filteredOrganizations = organizationLocations;

        if (selectedDisease) {
            // Filter affiliated entities for the selected disease
            filteredCenters = affiliatedCenters || [];
            filteredOtherCenters = affiliatedOtherCenters || [];
            filteredOrganizations = affiliatedPatientOrganizations || [];
        } else if (lowerCaseQuery) {
            // Filter AboutMap based on query
            filteredCenters = centerLocations.filter(center => center.Name.toLowerCase().includes(lowerCaseQuery));

            // Filter other AboutMap based on query
            filteredOtherCenters = otherCenterLocations.filter(center => center.ClinicName.toLowerCase().includes(lowerCaseQuery));

            // Filter organizations based on query
            filteredOrganizations = organizationLocations.filter(org => org.EnglishName.toLowerCase().includes(lowerCaseQuery));
        }

        // Update search results
        setSearchResults({
            diseases: filteredDiseases,
            centers: filteredCenters,
            otherCenters: filteredOtherCenters,
            organizations: filteredOrganizations,
        });
    }, [searchQuery, selectedDisease, diseasesData, centerLocations, otherCenterLocations, organizationLocations]);

    return (<div className="map-and-filter-container">
        <MapContainer center={[0, 0]} zoom={2} className="map-container" attributionControl={false}>
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBoundsOnLoad bounds={bounds} dataLoaded={dataLoaded}/>
            <MarkerClusterGroup ref={markerClusterGroupRef}

                                iconCreateFunction={(cluster) => {
                                    // Get the number of markers in the cluster
                                    const count = cluster.getChildCount();

                                    const color = '#b500a6';

                                    return L.divIcon({
                                        html: `<div style="
                background-color: ${color};
                color: white;
                border-radius: 50%;
                width: 30px;
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;">
                ${count}
                </div>`, className: 'custom-cluster-icon',
                                    });
                                }}>
                {/* Centers of Excellence markers */}
                {searchResults.centers.map((center, index) => (<Marker
                    key={index}
                    position={[center.Location.latitude, center.Location.longitude]}
                    icon={centerIcon}
                    ref={(ref) => (markersRef.current[center.ID] = ref)}
                >
                    <Popup>
                        <div className="popup-content">
                            <h3 className="popup-title">{center.Name}</h3>
                            {center.ClinicName && center.ClinicName.length > 0 && (
                                <p><strong>Κλινική/Τμήμα:</strong> {center.ClinicName}</p>)}
                            {center.Address && center.Address.length > 0 && (
                                <p><strong>Διεύθυνση:</strong> {center.Address}</p>)}
                            {center.Url && center.Url.length > 0 && (<a href={`${center.Url}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="orphanet-link">
                                Ιστοσελίδα
                            </a>)}
                            {center.Info && center.Info.length > 0 && (
                                <p><strong>Υποθεματικές Ενότητες:</strong> {center.Info}</p>)}
                            <p><strong><a href={center.GovGazette} target="_blank"
                                          rel="noopener noreferrer">ΦΕΚ</a></strong></p>
                            {center.Contact && (<div>
                                {center.Contact.Emails && center.Contact.Emails.length > 0 && (
                                    <p><strong>Emails:</strong> {center.Contact.Emails.join(', ')}</p>)}
                                {center.Contact.PhoneNumbers && center.Contact.PhoneNumbers.length > 0 && (
                                    <p><strong>Τηλέφωνα:</strong> {center.Contact.PhoneNumbers.join(', ')}</p>)}
                            </div>)}
                        </div>
                    </Popup>
                </Marker>))}

                {/* Other Centers markers */}
                {searchResults.otherCenters.map((center, index) => (<Marker
                    key={index}
                    position={[center.Location.latitude, center.Location.longitude]}
                    icon={otherCenterIcon}
                    ref={(ref) => (markersRef.current[center.ID] = ref)}
                >
                    <Popup>
                        <div className="popup-content">
                            <h3 className="popup-title">{center.ClinicName}</h3>
                            <p><strong>Νοσοκομείο:</strong> {center.HospitalName}
                            </p>
                            {center.Address && center.Address.length > 0 && (
                                <p><strong>Διεύθυνση:</strong> {center.Address}</p>)}
                            {center.Url && center.Url.length > 0 && (<a href={`${center.Url}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="orphanet-link">
                                Ιστοσελίδα
                            </a>)}
                            {center.Info && center.Info.length > 0 && (
                                <p><strong>Υποθεματικές Ενότητες:</strong> {center.Info}</p>)}
                            {center.Contact && (<div>
                                {center.Contact.Emails && center.Contact.Emails.length > 0 && (
                                    <p><strong>Emails:</strong> {center.Contact.Emails.join(', ')}</p>)}
                                {center.Contact.PhoneNumbers && center.Contact.PhoneNumbers.length > 0 && (
                                    <p><strong>Τηλέφωνα:</strong> {center.Contact.PhoneNumbers.join(', ')}</p>)}
                            </div>)}
                        </div>
                    </Popup>
                </Marker>))}
                {searchResults.organizations.map((org, index) => {
                    const isNonVerified = org.ID.startsWith('Δ');
                    const icon = isNonVerified ? nonVerifiedPatientOrganizationIcon : verifiedPatientOrganizationIcon;
                    return (<Marker
                        key={index}
                        position={[org.Location.latitude, org.Location.longitude]}
                        icon={icon}
                        ref={(ref) => (markersRef.current[org.ID] = ref)}
                    >
                        <Popup>
                            <div className="popup-content">
                                <h3>{org.GreekName}</h3>
                                {org.EnglishName && org.EnglishName.length > 0 && (
                                    <p><strong>English Name:</strong> {org.EnglishName}</p>)}
                                {org.Address && org.Address.length > 0 && (
                                    <p><strong>Διεύθυνση:</strong> {org.Address}</p>)}
                                {org.Url && org.Url.length > 0 && (
                                    <a href={org.Url.startsWith('http') ? org.Url : `https://${org.Url}`}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       className="orphanet-link">
                                        Ιστοσελίδα
                                    </a>)}
                                {org.Description && org.Description.length > 0 && (
                                    <p><strong>Περιγραφή:</strong> {org.Description}</p>)}
                                {org.Contact && (<div>
                                    {org.Contact.Emails && org.Contact.Emails.length > 0 && (
                                        <p><strong>Emails:</strong> {org.Contact.Emails.join(', ')}</p>)}
                                    {org.Contact.PhoneNumbers && org.Contact.PhoneNumbers.length > 0 && (
                                        <p><strong>Τηλέφωνα:</strong> {org.Contact.PhoneNumbers.join(', ')}</p>)}
                                </div>)}
                            </div>
                        </Popup>
                    </Marker>);
                })}
            </MarkerClusterGroup>
            {/*<MapLegend/>*/}
        </MapContainer>
        <div className="filter-sidebar">
            <div className="search-container">
                <div className="search-box">
                    {selectedDisease && (<div className="tag-container">
        <span className="tag">
            {(selectedDisease.GreekNames && selectedDisease.GreekNames[0]) || (selectedDisease.EnglishNames && selectedDisease.EnglishNames[0])}
            <button onClick={() => setSelectedDisease(null)}>x</button>
        </span>
                    </div>)}
                    <input
                        type="text"
                        placeholder="Αναζήτηση ασθενειών, κέντρων αριστείας και οργανώσεων ασθενών"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="search-input"
                    />
                </div>
            </div>
            <div className="search-results">
                <CollapsibleSection
                    title="Ασθένειες / Σύνδρομα"
                    itemCount={searchResults.diseases.length}
                    isOpen={isDiseasesOpen}
                    onToggle={() => setIsDiseasesOpen(!isDiseasesOpen)}
                >
                    <ul className="collapsible-list">
                        {searchResults.diseases.map((disease, index) => (
                            <li key={index} className="collapsible-list-item"
                                onClick={() => handleDiseaseSelect(disease)} style={{cursor: 'pointer'}}>
                                <div className="item-content">
                                    <span
                                        className="item-title">{(disease.GreekNames && disease.GreekNames[0]) || (disease.EnglishNames && disease.EnglishNames[0])}</span>
                                </div>
                            </li>))}
                    </ul>
                </CollapsibleSection>
                <CollapsibleSection
                    title="Κέντρα Εμπειρογνωμοσύνης"
                    itemCount={searchResults.centers.length}
                    isOpen={isCentersOpen}
                    legendIcons={["hospital.svg"]}
                    onToggle={() => setIsCentersOpen(!isCentersOpen)}
                >
                    <ul className="collapsible-list">
                        {searchResults.centers.map((center, index) => (<li
                            key={index}
                            className="collapsible-list-item"
                            onClick={() => handleMarkerClick(center)}
                            style={{cursor: 'pointer'}}
                        >
                            <div className="item-content">
                                <span className="item-title">{center.Name}</span>
                            </div>
                        </li>))}
                    </ul>
                </CollapsibleSection>
                <CollapsibleSection
                    title="Ειδικές Μονάδες / Ιατρεία"
                    itemCount={searchResults.otherCenters.length}
                    isOpen={isOtherCentersOpen}
                    legendIcons={["oc.svg"]}
                    onToggle={() => setIsOtherCentersOpen(!isOtherCentersOpen)}
                >
                    <ul className="collapsible-list">
                        {searchResults.otherCenters.map((center, index) => (<li
                            key={index}
                            className="collapsible-list-item"
                            onClick={() => handleMarkerClick(center)}
                            style={{cursor: 'pointer'}} // Set cursor to pointer
                        >
                            <div className="item-content">
                                <span className="item-title">{center.ClinicName}</span>
                            </div>
                        </li>))}
                    </ul>
                </CollapsibleSection>
                <CollapsibleSection
                    title="Οργανώσεις Ασθενών"
                    itemCount={searchResults.organizations.length}
                    isOpen={isOrganizationsOpen}
                    legendIcons={["vpo.svg", "nvpo.svg"]}
                    onToggle={() => setIsOrganizationsOpen(!isOrganizationsOpen)}
                >
                    <ul className="collapsible-list">
                        {searchResults.organizations.map((org, index) => (
                            <li key={index} className="collapsible-list-item"
                                onClick={() => handleMarkerClick(org)}
                                style={{cursor: "pointer"}}>
                                <div className="item-content">
                                    <span className="item-title">{org.GreekName}</span>
                                </div>
                            </li>))}
                    </ul>
                </CollapsibleSection>
                {lastUpdated && (<div className="filter-watermark">
                    Τελευταία ενημέρωση: {new Date(lastUpdated).toLocaleDateString("el-GR", {
                    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
                </div>)}
            </div>
        </div>
        <div className={`info-sidebar ${!user ? 'blocked' : ''}`}>{renderRightPanelContent()}{!user && (
            <div className="protected-overlay">
                <div className="overlay-content">
                    <p>Για να δείτε περισσότερες πληροφορίες, παρακαλώ εγγραφείτε ή συνδεθείτε.</p>
                    <button onClick={() => navigate('/login')}>Σύνδεση / Εγγραφή</button>
                </div>
            </div>)}</div>
    </div>);
};

export default MapComponent;